import { Check } from 'lucide-react'

interface EditionSelectorProps {
  availableEditions: number[]
  selectedEdition: number | null
  onChange: (edition: number | null) => void
  latestEdition?: number
  loading?: boolean
}

export default function EditionSelector({
  availableEditions,
  selectedEdition,
  onChange,
  latestEdition,
  loading = false
}: EditionSelectorProps) {
  if (loading) {
    return (
      <div className="card-dark p-4">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
          <span className="text-sm text-text-inverse/70">Lade Editionen...</span>
        </div>
      </div>
    )
  }

  if (availableEditions.length === 0) {
    return (
      <div className="card-dark p-4">
        <span className="text-sm text-text-inverse/70">Keine historischen Daten</span>
      </div>
    )
  }

  const latestEd = latestEdition || availableEditions[0]

  return (
    <div className="card-dark">
      {/* Header */}
      <div className="px-4 py-3 border-b border-text-inverse/10">
        <label className="text-xs font-semibold text-primary uppercase tracking-wider">
          Edition wählen
        </label>
      </div>

      {/* Edition Buttons */}
      <div className="p-3 space-y-2">
        {/* Aktuell Button - represents the latest edition */}
        <button
          onClick={() => onChange(null)}
          className={`w-full text-left px-3 py-2 rounded-gut8 text-sm font-medium transition-all ${
            selectedEdition === null || selectedEdition === latestEd
              ? 'bg-primary text-text-inverse'
              : 'text-text-inverse/80 hover:bg-text-inverse/10'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span>Aktuell</span>
                {(selectedEdition === null || selectedEdition === latestEd) && <Check className="w-4 h-4" />}
              </div>
              <div className="text-xs opacity-70 mt-0.5">
                Edition {latestEd}
              </div>
            </div>
          </div>
        </button>

        {/* Edition Buttons - only show older editions (not the latest, since that's covered by "Aktuell") */}
        {availableEditions
          .filter((edition) => edition !== latestEd)
          .map((edition) => (
            <button
              key={edition}
              onClick={() => onChange(edition)}
              className={`w-full text-left px-3 py-2 rounded-gut8 text-sm font-medium transition-all ${
                selectedEdition === edition
                  ? 'bg-primary text-text-inverse'
                  : 'text-text-inverse/80 hover:bg-text-inverse/10'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span>{edition}</span>
                    {selectedEdition === edition && <Check className="w-4 h-4" />}
                  </div>
                </div>
              </div>
            </button>
          ))}
      </div>

      {/* Info Footer */}
      <div className="px-4 py-3 border-t border-text-inverse/10">
        <p className="text-xs text-text-inverse/60">
          <span className="font-medium text-primary">
            Edition {selectedEdition ?? latestEd}
          </span>
          <br />
          <span className="text-text-inverse/50">
            Alle Daten dieser Jahresausgabe
          </span>
        </p>
      </div>
    </div>
  )
}
